import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import viewIcon from "../../assets/images/policies-manuals/view-icon.svg";
import editIcon from "../../assets/images/policies-manuals/edit-icon.svg";
import downloadIcon from "../../assets/images/policies-manuals/download-icon.svg";
import images from "../../assets/images";
import Modal from "../../components/modal/Modal";
import ConfirmModal from "../../components/modal/ConfirmModal";
import {
  allFolders,
  createFolders,
  updatefilefolder,
  deletefilefolder,
  searchPolicies,
  getFolderContents,
  downloadFolder,
} from "../../service/api";
import { SpinLoader } from "../../components/loader/Loader";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { usePermissions } from "../../hooks/usePermission";
import { sanitizeName } from "../../common/utils";
import {
  PoliciesModuleConfig,
  fireSafetyConfig,
  folderPathByPolicyType,
} from "./policiesConfig";

interface folderType {
  createdAt: number;
  updatedAt: number;
  id: string;
  folderName: string;
  files: null | any[];
  createdDate: string;
  uploadedBy: string;
  totalsize?: string;
  status?: string;
  reference?: string;
  type?: string;
}

interface PoliciesAndManualsProps {
  config?: PoliciesModuleConfig;
}

// Type badge display/styling, only rendered for the "certificate" variant (Laws & Regulations)
const getTypeDisplay = (type?: string) => {
  switch (type?.toLowerCase()) {
    case "fire_safety":
      return "Fire Safety";
    case "laws_and_regulations":
      return "Laws & Regulations";
    case "hazardous_materials":
      return "Hazardous Materials";
    default:
      return "Laws & Regulations";
  }
};

const getTypeStyling = (type?: string) => {
  switch (type?.toLowerCase()) {
    case "fire_safety":
      return "bg-green-100 text-green-800 border border-green-200";
    case "laws_and_regulations":
      return "bg-blue-100 text-blue-800 border border-blue-200";
    case "hazardous_materials":
      return "bg-orange-100 text-orange-800 border border-orange-200";
    default:
      return "bg-blue-100 text-blue-800 border border-blue-200";
  }
};

const PoliciesAndManuals = ({
  config = fireSafetyConfig,
}: PoliciesAndManualsProps) => {
  const isCertificate = config.variant === "certificate";
  const { policyType, permissionModule, folderPath, labels } = config;
  const nameFieldLabel = `${labels.entitySingular} Name`;
  const namePlaceholder = `Enter ${labels.entitySingular} Name`;
  const emptyNameMessage = `${labels.entitySingular} name cannot be empty`;
  const editModalTitle = `Edit ${labels.entitySingular}`;
  const searchPlaceholder = `Search ${labels.entityPlural}`;

  const [visible, setVisible] = React.useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [resMessage, setResMessage] = useState<string>("");
  const [createModalMessage, setCreateModalMessage] = useState<string>(""); // Message for Create Folder modal
  const [editModalMessage, setEditModalMessage] = useState<string>(""); // Message for Edit Folder modal
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [folderName, setFolderName] = useState<string>("");
  const [editFolder, setEditFolder] = useState<folderType | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<folderType | null>(null);
  const navigate = useNavigate();
  const [folders, setFolders] = useState<folderType[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] =
    useState<string>("ALL Premises");

  const { canCreate, canRead, canUpdate, canDelete } = usePermissions();
  const onClose = () => {
    setVisible(false);
    setCreateModalMessage(""); // Clear message when closing the modal
    setFolderName(""); // Clear folder name input
  };

  const onEditClose = () => {
    setEditVisible(false);
    setEditFolder(null);
    setFolderName("");
    setEditModalMessage(""); // Clear message when closing the edit modal
  };

  const isForeignFolder = (folder: folderType) =>
    !!config.showAllTypes && folder.type !== policyType;

  const handleFolderClick = (folder: folderType) => {
    const targetPath =
      isForeignFolder(folder) && folder.type
        ? folderPathByPolicyType[
            folder.type as PoliciesModuleConfig["policyType"]
          ]
        : folderPath;
    navigate(`${targetPath}/${folder.id}`, {
      state: { selectedRegion, selectedPremises },
    });
  };

  const createFolder = async () => {
    if (!folderName.trim()) {
      setCreateModalMessage(emptyNameMessage);
      return;
    }

    // Check for duplicate folder name (case-insensitive)
    const duplicateFolder = folders.find(
      (folder) =>
        folder.folderName.toLowerCase() === folderName.trim().toLowerCase(),
    );
    if (duplicateFolder) {
      setCreateModalMessage(labels.duplicateNameMessage);
      return;
    }

    // Clear any previous message
    setCreateModalMessage("");
    setVisible(false);
    setIsLoading(true);
    const data = {
      policyType,
      details: [
        {
          folderName,
          createdDate: new Date().toISOString(),
          uploadedBy: "current_user",
        },
      ],
    };
    try {
      const res = await createFolders(data);
      if (res.data.resCode === 200) {
        setResMessage(res.data.resMessage);
        fetchFoldersWithFilters();
      } else {
        setResMessage(res.data.resMessage || "Failed to create folder");
      }
    } catch (err: any) {
      setResMessage(err.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
      setFolderName("");
    }
  };

  const formatSize = (sizeInKB: number): string => {
    if (sizeInKB === 0) return "0 MB";
    const sizeInMB = sizeInKB / 1024;
    if (sizeInMB < 1024) {
      return `${sizeInMB.toFixed(2)} MB`;
    }
    const sizeInGB = sizeInMB / 1024;
    return `${sizeInGB.toFixed(2)} GB`;
  };

  const fetchFoldersWithFilters = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: { [key: string]: any; keyword?: string } = {};
      const isSearchActive =
        searchQuery.trim() ||
        (selectedRegion && selectedRegion !== "ALL Regions") ||
        (selectedPremises && selectedPremises !== "ALL Premises");

      if (searchQuery.trim()) params.keyword = searchQuery;
      if (selectedRegion && selectedRegion !== "ALL Regions")
        params.region = selectedRegion;
      if (selectedPremises && selectedPremises !== "ALL Premises")
        params.premises = selectedPremises;

      // "all" is a sentinel the backend recognizes to skip its policyType filter entirely —
      // omitting the param isn't enough, since the backend defaults missing policyType to fire_safety.
      const effectivePolicyType = config.showAllTypes ? "all" : policyType;
      const res = isSearchActive
        ? await searchPolicies({ ...params, policyType: effectivePolicyType })
        : await allFolders({ ...params, policyType: effectivePolicyType });

      if (res.data.resCode === 200) {
        const effectiveRegion = selectedRegion || "ALL Regions";
        const isAllRegionsAndPremises =
          effectiveRegion === "ALL Regions" &&
          selectedPremises === "ALL Premises";
        const folderData = isSearchActive
          ? res.data.message
          : res.data.message.results;

        // showAllTypes: show every module's folders. Otherwise, restrict to this module's own
        // type (untyped legacy folders are treated as belonging to this module for backward compatibility).
        const typedFolders = config.showAllTypes
          ? folderData
          : folderData.filter((folder: any) => {
              return folder.policyType === policyType || !folder.policyType;
            });

        const folderPromises = typedFolders.map(async (folder: any) => {
          try {
            let filesWithSizes = folder.files || [];
            let totalSize = 0;

            if (!isAllRegionsAndPremises) {
              const contentsParams = {
                region: params.region,
                premises: params.premises,
              };
              const contentsRes = await getFolderContents(
                folder.id,
                contentsParams,
              );
              filesWithSizes = contentsRes.data.message?.files || [];

              if (filesWithSizes.length === 0) {
                return null;
              }

              totalSize = filesWithSizes.reduce((total: number, file: any) => {
                const sizeInKB = parseInt(file.size, 10) || 0;
                return total + sizeInKB;
              }, 0);
            } else {
              totalSize = filesWithSizes.reduce((total: number, file: any) => {
                const sizeInKB = parseInt(file.size, 10) || 0;
                return total + sizeInKB;
              }, 0);
            }

            const formattedSize = formatSize(totalSize);
            const formattedFolder: folderType = {
              id: folder.id,
              folderName: folder.folderName,
              files: filesWithSizes,
              totalsize: formattedSize,
              createdAt: folder.createdAt || Date.now(),
              updatedAt: folder.updatedAt || Date.now(),
              createdDate: folder.createdDate || new Date().toISOString(),
              uploadedBy: folder.uploadedBy || "unknown",
              status: folder.status || "Active",
              reference: folder.reference || "",
              // Untyped legacy folders (created before policyType existed) are Fire Safety's own
              type: folder.policyType || folder.type || "fire_safety",
            };
            return formattedFolder;
          } catch (err) {
            console.error(`Failed to process folder ${folder.id}:`, err);
            return null;
          }
        });

        const foldersWithSizes = (await Promise.all(folderPromises)).filter(
          (folder) => folder !== null,
        ) as folderType[];
        setFolders(foldersWithSizes);
      } else {
        setFolders([]);
        setResMessage(res.data.resMessage || "Failed to fetch folders");
      }
    } catch (err: any) {
      console.error("Fetch error:", err);
      setFolders([]);
      setResMessage(err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  }, [
    searchQuery,
    selectedRegion,
    selectedPremises,
    policyType,
    labels.entityPlural,
  ]);

  const handleEditFolder = async () => {
    if (!editFolder || !folderName.trim()) {
      setEditModalMessage(emptyNameMessage);
      return;
    }

    // Check for duplicate folder name during edit (excluding the folder being edited)
    const duplicateFolder = folders.find(
      (folder) =>
        folder.id !== editFolder.id &&
        folder.folderName.toLowerCase() === folderName.trim().toLowerCase(),
    );
    if (duplicateFolder) {
      setEditModalMessage(labels.duplicateNameMessage);
      return;
    }

    // Clear any previous message
    setEditModalMessage("");
    setEditVisible(false);
    setIsLoading(true);
    const data = { folderName, policyType };
    try {
      const res = await updatefilefolder(data, editFolder.id);
      if (res.data.resCode === 200) {
        setResMessage(res.data.resMessage || "Folder updated successfully");
        setFolders(
          folders.map((folder) =>
            folder.id === editFolder.id ? { ...folder, folderName } : folder,
          ),
        );
      } else {
        // Don't show database errors to user, just log them
        console.error("Folder update failed:", res.data.resMessage);
        setResMessage(""); // Clear any existing error message
      }
    } catch (err: any) {
      // Don't show server errors to user, just log them
      console.error("Folder update error:", err.message);
      setResMessage(""); // Clear any existing error message
    } finally {
      setIsLoading(false);
      setEditFolder(null);
      setFolderName("");
    }
  };

  const handleDownloadFolder = async (folderId: string, folderName: string) => {
    try {
      setIsLoading(true);
      const response = await downloadFolder(folderId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${folderName}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setResMessage(err?.message ?? "Failed to download folder");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFolder = (folder: folderType) => {
    setFolderToDelete(folder);
  };

  const confirmDeleteFolder = async () => {
    const folder = folderToDelete;
    if (!folder) return;
    setFolderToDelete(null);
    try {
      setIsLoading(true);
      const res = await deletefilefolder(folder.id);
      if (res.data.resCode === 200) {
        setResMessage(res.data.resMessage || "Folder deleted successfully");
        fetchFoldersWithFilters();
      } else {
        setResMessage(res.data.resMessage || "Failed to delete folder");
      }
    } catch (err: any) {
      setResMessage(err?.message ?? "Failed to delete folder");
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (folder: folderType) => {
    setEditFolder(folder);
    setFolderName(folder.folderName);
    setEditVisible(true);
  };

  useEffect(() => {
    const debounceFetch = setTimeout(() => {
      fetchFoldersWithFilters();
    }, 300);

    return () => clearTimeout(debounceFetch);
  }, [fetchFoldersWithFilters, searchQuery, selectedRegion, selectedPremises]);

  return (
    <div className="px-2 py-4 min-h-screen mt-20">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{labels.pageTitle}</h1>
        {canCreate(permissionModule) && (
          <button
            className="flex items-center px-4 py-2 bg-teal-500 text-white rounded-full"
            onClick={() => setVisible(true)}
          >
            <img
              src={images.createFolderIcon}
              alt="Create"
              className="w-5 h-5 mr-2"
            />
            {labels.createButtonLabel}
          </button>
        )}
      </div>
      <div className="flex gap-4 mb-4">
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder={searchPlaceholder}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <img
            src={images.searchIcon}
            alt="Search"
            className="absolute left-3 top-2.5 w-5 h-5"
          />
        </div>
        <Dropdown
          selectedValue={selectedRegion}
          onChange={setSelectedRegion}
          type="region"
        />
        <Dropdown
          selectedValue={selectedPremises}
          onChange={setSelectedPremises}
          type="premises"
          selectedRegion={selectedRegion}
        />
      </div>
      {/* Display resMessage for non-modal messages (e.g., API errors) */}
      {resMessage && (
        <p className="text-center py-2 text-gray-600">{resMessage}</p>
      )}
      <div className="bg-white p-4 rounded-lg shadow-lg">
        {isLoading ? (
          <div className="flex justify-center">
            <SpinLoader />
          </div>
        ) : folders.length > 0 ? (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b">
                <th className="py-2">
                  {isCertificate ? "Laws/Acts/Regulations" : "Folders"}
                </th>
                {isCertificate && <th className="py-2">Type</th>}
                <th className="py-2">
                  {isCertificate ? "Certificates/References" : "No of Files"}
                </th>
                <th className="py-2">Folder Size</th>
                <th className="py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {folders.map((folder, index) => {
                const foreign = isForeignFolder(folder);
                return (
                  <tr key={index} className="border-b">
                    <td className="py-2 flex items-center">
                      <img
                        src={images.folderIcon}
                        alt="Folder"
                        className="w-5 h-5 mr-2"
                      />
                      <span
                        className="cursor-pointer text-black hover:text-[#0fa9a6] active:text-[#0fa9a6] transition-colors"
                        onClick={() => handleFolderClick(folder)}
                      >
                        {folder.folderName}
                      </span>
                    </td>
                    {isCertificate && (
                      <td className="py-2">
                        <span
                          className={`px-3 py-1.5 rounded-full text-xs font-medium ${getTypeStyling(
                            folder.type,
                          )}`}
                        >
                          {getTypeDisplay(folder.type)}
                        </span>
                      </td>
                    )}
                    <td className="py-2">
                      {folder.files?.length || 0} File(s)
                    </td>
                    <td className="py-2">{folder.totalsize || "0 MB"}</td>
                    <td className="py-2 flex gap-3">
                      {canRead(permissionModule) && (
                        <img
                          src={viewIcon}
                          alt="View"
                          className="w-5 h-5 cursor-pointer"
                          onClick={() => handleFolderClick(folder)}
                        />
                      )}
                      {!foreign && canUpdate(permissionModule) && (
                        <img
                          src={editIcon}
                          alt="Edit"
                          className="w-5 h-5 cursor-pointer"
                          onClick={() => openEditModal(folder)}
                        />
                      )}
                      {canRead(permissionModule) && (
                        <img
                          src={downloadIcon}
                          alt="Download"
                          className="w-5 h-5 cursor-pointer"
                          onClick={() =>
                            handleDownloadFolder(folder.id, folder.folderName)
                          }
                        />
                      )}
                      {!foreign && canDelete(permissionModule) && (
                        <Trash2
                          className="w-5 h-5 cursor-pointer text-red-500"
                          onClick={() => handleDeleteFolder(folder)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-center py-4">
            {searchQuery.trim() || selectedRegion || selectedPremises
              ? `No ${labels.entityPlural} found matching your search criteria`
              : `No ${labels.entityPlural} found`}
          </p>
        )}
      </div>
      <Modal isOpen={visible} onClose={onClose}>
        <div className="bg-white w-[500px] rounded-xl overflow-hidden">
          <div className="p-6 leading-none">
            <h1 className="text-xl font-bold">{labels.createModalTitle}</h1>
            <hr className="my-2" />
            <div className="flex flex-col gap-4 mt-4">
              {/* Flexbox layout to place heading and message side by side */}
              <div className="flex items-center gap-3">
                <label htmlFor="folderName" className="font-medium">
                  {nameFieldLabel}
                </label>
                {createModalMessage && (
                  <p
                    className={`text-sm flex-shrink-0 ${
                      createModalMessage.includes("already exists")
                        ? "text-red-500"
                        : "text-gray-600"
                    }`}
                  >
                    {createModalMessage}
                  </p>
                )}
              </div>
              <input
                type="text"
                id="folderName"
                className="border-2 p-2 rounded"
                placeholder={namePlaceholder}
                value={folderName}
                onChange={(e) => setFolderName(sanitizeName(e.target.value))}
              />
            </div>
          </div>
          <div className="flex bg-[#61748E] justify-end py-3 pr-2 gap-4">
            <button
              className="bg-[#0fa9a6] px-3 py-2 rounded-full text-white"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="bg-[#869ab6] px-3 py-2 rounded-full text-white"
              onClick={createFolder}
            >
              Create
            </button>
          </div>
        </div>
      </Modal>
      <Modal isOpen={editVisible} onClose={onEditClose}>
        <div className="bg-white w-[500px] rounded-xl overflow-hidden">
          <div className="p-6 leading-none">
            <h1 className="text-xl font-bold">{editModalTitle}</h1>
            <hr className="my-2" />
            <div className="flex flex-col gap-4 mt-4">
              {/* Flexbox layout to place heading and message side by side */}
              <div className="flex items-center gap-3">
                <label htmlFor="editFolderName" className="font-medium">
                  {nameFieldLabel}
                </label>
                {editModalMessage && (
                  <p
                    className={`text-sm flex-shrink-0 ${
                      editModalMessage.includes("already exists")
                        ? "text-red-500"
                        : "text-gray-600"
                    }`}
                  >
                    {editModalMessage}
                  </p>
                )}
              </div>
              <input
                type="text"
                id="editFolderName"
                className="border-2 p-2 rounded"
                placeholder={namePlaceholder}
                value={folderName}
                onChange={(e) => setFolderName(sanitizeName(e.target.value))}
              />
            </div>
          </div>
          <div className="flex bg-[#61748E] justify-end py-3 pr-2 gap-4">
            <button
              className="bg-[#0fa9a6] px-3 py-2 rounded-full text-white"
              onClick={onEditClose}
            >
              Cancel
            </button>
            <button
              className="bg-[#869ab6] px-3 py-2 rounded-full text-white"
              onClick={handleEditFolder}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
      <ConfirmModal
        isOpen={!!folderToDelete}
        title={`Are you sure you want to delete "${folderToDelete?.folderName}"?`}
        message="All files in it and any related tasks will also be deleted."
        onConfirm={confirmDeleteFolder}
        onCancel={() => setFolderToDelete(null)}
      />
    </div>
  );
};

export default PoliciesAndManuals;
